import { DropResult } from '@hello-pangea/dnd';
import { useCallback, useMemo } from 'react';
import { hashMap } from '@/utils/shared/hashMap';
import { DragDropAnswerContextValue } from '@/components/question/view/primitives/shared/dnd/DragDropAnswerContext';
import {
  buildStringPlacement,
  toPlacement,
} from '@/utils/questions/dnd/placementSerialization';
import {
  capacityOf,
  resolvePlacement,
} from '@/utils/questions/dnd/resolvePlacement';
import {
  BANK_ID,
  DnDToken,
  Placement,
  SlotCapacity,
  SlotConfig,
} from '@/utils/questions/dnd/types';
import { useClickPlacement } from './useClickPlacement';
import { usePlacementFlip } from './usePlacementFlip';
import compact from 'lodash/compact';

export type UseDragDropAnswerArgs = {
  tokens: DnDToken[];
  slots: SlotConfig[];
  placement: Placement;
  onPlacementChange?: (next: Placement) => void;
  disabled?: boolean;
};

export type UseDragDropAnswerReturn = DragDropAnswerContextValue & {
  onDragEnd: (result: DropResult) => void;
};

export function useDragDropAnswer({
  tokens,
  slots,
  placement,
  onPlacementChange,
  disabled = false,
}: UseDragDropAnswerArgs): UseDragDropAnswerReturn {
  const tokenById = useMemo(
    () => hashMap(tokens, token => String(token.id)),
    [tokens],
  );

  const stringPlacement = useMemo(
    () => buildStringPlacement({ tokens, slots, placement }),
    [slots, placement, tokens],
  );

  const bankTokens = useMemo(
    () => compact(stringPlacement[BANK_ID]?.map(id => tokenById.get(id))),
    [stringPlacement, tokenById],
  );

  const tokensOf = useCallback(
    (slotId: string) =>
      compact(stringPlacement[slotId]?.map(id => tokenById.get(id))),
    [stringPlacement, tokenById],
  );

  const capacityOfInternal = useCallback(
    (slotId: string): SlotCapacity => capacityOf(slots, slotId),
    [slots],
  );

  const getToken = useCallback((id: string) => tokenById.get(id), [tokenById]);

  const { flipFrom, clearFlip, captureMoves } = usePlacementFlip();

  const applyDrop = useCallback(
    (result: DropResult, exclude?: string) => {
      if (disabled || !onPlacementChange) return;

      const next = resolvePlacement({
        placement: stringPlacement,
        slots,
        result,
      });
      if (next === stringPlacement) return;

      captureMoves({ previous: stringPlacement, next, exclude });

      onPlacementChange(toPlacement(next, tokenById));
    },
    [
      disabled,
      onPlacementChange,
      stringPlacement,
      slots,
      tokenById,
      captureMoves,
    ],
  );

  const onDragEnd = useCallback(
    (result: DropResult) => applyDrop(result, result.draggableId),
    [applyDrop],
  );

  const { selectedId, clearSelection, onChipClick, onContainerClick } =
    useClickPlacement({ stringPlacement, applyDrop, disabled });

  return {
    bankTokens,
    tokensOf,
    capacityOf: capacityOfInternal,
    getToken,
    disabled,
    flipFrom,
    clearFlip,
    selectedId,
    clearSelection,
    onChipClick,
    onContainerClick,
    onDragEnd,
  };
}
